/* Sanosis crisp — behaviour layer for inner pages.
   1. Editorial documents: builds the contents rail from H2 headings, scroll-spy, copy-email button.
   2. Shopify legal pages (/policies/*): restructures the built-in markup into the same document layout.
   3. Scroll reveal for cards and content blocks; reading-progress bar on long-form pages.
   Everything degrades to plain static content when JS or motion is unavailable. */
(function () {
  var doc = document.documentElement;
  var body = document.body;
  if (!body) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isHome = body.classList.contains('sx-template-index');

  function slug(s) {
    return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'section';
  }

  /* ---------- 1. Editorial documents ---------- */
  function initDoc(root) {
    if (root.dataset.sxpInit) return;
    root.dataset.sxpInit = '1';
    var bodyEl = root.querySelector('[data-sxp-body]');
    var toc = root.querySelector('[data-sxp-toc]');
    root.querySelectorAll('[data-sxp-copy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var t = btn.getAttribute('data-sxp-copy');
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(t).then(function () {
          var was = btn.textContent;
          btn.textContent = 'Copied';
          setTimeout(function () { btn.textContent = was; }, 1600);
        });
      });
    });
    if (!bodyEl || !toc) return;
    var level = bodyEl.getAttribute('data-sxp-level') || 'h2';
    var heads = Array.prototype.slice.call(bodyEl.querySelectorAll(level)).filter(function (h) { return h.textContent.trim().length > 0; });
    if (heads.length < 2) { toc.hidden = true; return; }
    /* Content that already numbers its own headings ("1. Intended Use") keeps its numbers */
    var pre = heads.filter(function (h) { return /^\d+[.)]\s/.test(h.textContent.trim()); }).length >= 2;
    if (pre) bodyEl.classList.remove('sxp-body--numbered');
    var ol = toc.querySelector('ol');
    var used = {};
    heads.forEach(function (h, i) {
      var id = h.id || slug(h.textContent);
      while (used[id] || (document.getElementById(id) && document.getElementById(id) !== h)) id = id + '-' + (i + 1);
      used[id] = true;
      h.id = id;
      var label = h.textContent.trim();
      var num = String(i + 1);
      if (pre) {
        var m = label.match(/^(\d+)[.)]\s*(.*)$/);
        if (m) { num = m[1]; label = m[2]; } else { num = '·'; }
      }
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + id;
      var n = document.createElement('i');
      n.textContent = num;
      a.appendChild(n);
      a.appendChild(document.createTextNode(label));
      li.appendChild(a);
      ol.appendChild(li);
    });
    var links = ol.querySelectorAll('a');
    function setOn(idx) { links.forEach(function (l, j) { l.classList.toggle('is-on', j === idx); }); }
    setOn(0);
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) setOn(heads.indexOf(e.target)); });
      }, { rootMargin: '-20% 0px -65% 0px', threshold: 0 });
      heads.forEach(function (h) { io.observe(h); });
    }
  }

  /* ---------- 2. Legal policy pages ---------- */
  /* Editors leave <p>&nbsp;</p>, <p><br></p> and trailing <br>s behind; they read as double gaps.
     Done in the DOM (not CSS) so a <br> that sits between two lines of text is left alone. */
  function tidyRte(scope) {
    (scope || document).querySelectorAll('.rte').forEach(function (rte) {
      rte.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6').forEach(function (el) {
        var n = el.lastChild;
        while (n && ((n.nodeType === 3 && !n.textContent.replace(/[\s ]/g, '')) || (n.nodeType === 1 && n.tagName === 'BR'))) {
          var prev = n.previousSibling; el.removeChild(n); n = prev;
        }
        if (el.tagName !== 'LI' && !el.textContent.replace(/[\s ]/g, '') && !el.querySelector('img, iframe, video, svg, table')) el.remove();
      });
      /* a rule immediately before a heading doubles the heading's own rule — drop it */
      rte.querySelectorAll('hr').forEach(function (hr) {
        var nx = hr.nextElementSibling;
        if (!nx || /^H[1-6]$/.test(nx.tagName)) hr.remove();
      });
      /* trailing <br> inside an inline wrapper (<strong>…<br></strong>) */
      rte.querySelectorAll('p > :is(strong, b, em, i, span):last-child').forEach(function (el) {
        var n = el.lastChild;
        while (n && n.nodeType === 1 && n.tagName === 'BR') { var prev = n.previousSibling; el.removeChild(n); n = prev; }
      });
    });
  }

  function upgradePolicy() {
    var pc = document.querySelector('.shopify-policy__container');
    if (!pc || pc.dataset.sxpInit) return;
    var titleWrap = pc.querySelector('.shopify-policy__title');
    var bodyWrap = pc.querySelector('.shopify-policy__body');
    var h1 = titleWrap && titleWrap.querySelector('h1');
    var rte = bodyWrap && (bodyWrap.querySelector('.rte') || bodyWrap);
    if (!h1 || !rte) return;

    pc.classList.add('sxp');
    pc.setAttribute('data-sxp', '');

    /* hero */
    var hero = document.createElement('header');
    hero.className = 'sxp-hero';
    var crumbs = document.createElement('nav');
    crumbs.className = 'sxp-crumbs';
    crumbs.setAttribute('aria-label', 'Breadcrumb');
    crumbs.innerHTML = '<a href="/">Home</a><span aria-hidden="true">/</span><span>Policies</span><span aria-hidden="true">/</span><b aria-current="page"></b>';
    crumbs.querySelector('b').textContent = h1.textContent.trim();
    h1.classList.add('sxp-title');
    hero.appendChild(crumbs);
    hero.appendChild(h1);

    /* lift a short first paragraph as the lede */
    var first = rte.firstElementChild;
    if (first && first.tagName === 'P') {
      var txt = first.textContent.trim();
      if (txt.length > 0 && txt.length < 320 && !first.querySelector('img, table')) {
        first.className = 'sxp-lede';
        hero.appendChild(first);
      }
    }
    var level = ['h2', 'h3', 'h4'].filter(function (l) { return rte.querySelectorAll(l).length > 1; })[0] || 'h2';
    rte.setAttribute('data-sxp-level', level);
    var heads = rte.querySelectorAll(level);
    var words = rte.textContent.trim().split(/\s+/).length;
    var meta = document.createElement('div');
    meta.className = 'sxp-meta';
    meta.innerHTML = '<span>Sanosis Pty Ltd</span><span>' + (heads.length ? heads.length + ' sections · ' : '') + '<b>' + Math.max(1, Math.ceil(words / 210)) + ' min read</b></span>';
    hero.appendChild(meta);

    /* document grid */
    var grid = document.createElement('div');
    grid.className = 'sxp-doc sxp-doc--rail';
    var aside = document.createElement('aside');
    aside.className = 'sxp-rail';
    aside.innerHTML =
      '<div class="sxp-rail__inner">' +
        '<div class="sxp-toc" data-sxp-toc><span class="sxp-eyebrow">Contents</span><ol></ol></div>' +
        '<div class="sxp-help"><b>Need a hand?</b><p>Our support team answers every message.</p>' +
          '<div class="sxp-help__mail"><a href="mailto:contact@sanosis.com">contact@sanosis.com</a><button type="button" data-sxp-copy="contact@sanosis.com">Copy</button></div></div>' +
      '</div>';
    rte.classList.add('sxp-body', 'rte', 'sxp-list-tiles', 'sxp-body--numbered');
    rte.setAttribute('data-sxp-body', '');

    while (pc.firstChild) pc.removeChild(pc.firstChild);
    grid.appendChild(aside);
    grid.appendChild(rte);
    pc.appendChild(hero);
    pc.appendChild(grid);

    /* wrap tables so they can scroll on small screens */
    rte.querySelectorAll('table').forEach(function (t) {
      if (t.parentElement && t.parentElement.classList.contains('rte-table-wrapper')) return;
      var w = document.createElement('div');
      w.className = 'rte-table-wrapper';
      t.parentNode.insertBefore(w, t);
      w.appendChild(t);
    });
    initDoc(pc);
  }

  /* ---------- 3. Reveal + progress ---------- */
  var targets = [
    '.product-grid .product-card',
    '.resource-list .product-card',
    '.resource-list .resource-card',
    '.resource-list .featured-blog-posts-card',
    '.blog-post-card',
    '.accordion',
    '.sxp-body > h2',
    '.sxa-chapter',
    '.sxc-tile',
    '.sx-template-article .blog-post-content > h2'
  ].join(',');

  var io = null;
  function arm() {
    if (isHome || reduce) return;
    doc.classList.add('sx-js');
    if (!io && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('sx-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    var i = 0;
    document.querySelectorAll(targets).forEach(function (el) {
      if (el.classList.contains('sx-reveal')) return;
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.9) return; /* above the fold stays put */
      el.classList.add('sx-reveal');
      el.style.animationDelay = (Math.min(i++, 6) * 60) + 'ms';
      if (io) io.observe(el); else el.classList.add('sx-in');
    });
  }

  function readbar() {
    if (reduce) return;
    var longform = body.classList.contains('sx-template-article') || document.querySelector('[data-sxp], .sxa');
    if (!longform || document.querySelector('.sx-readbar')) return;
    var bar = document.createElement('div');
    bar.className = 'sx-readbar';
    bar.setAttribute('aria-hidden', 'true');
    var fill = document.createElement('i');
    bar.appendChild(fill);
    body.appendChild(bar);
    var ticking = false;
    function update() {
      ticking = false;
      var h = doc.scrollHeight - window.innerHeight;
      var p = h > 0 ? Math.min(1, Math.max(0, window.scrollY / h)) : 0;
      fill.style.transform = 'scaleX(' + p.toFixed(4) + ')';
    }
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  }

  function init() {
    tidyRte();
    upgradePolicy();
    document.querySelectorAll('[data-sxp]').forEach(initDoc);
    if (!doc.classList.contains('shopify-design-mode')) arm();
    readbar();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('shopify:section:load', function (e) {
    tidyRte(e.target);
    document.querySelectorAll('[data-sxp]').forEach(initDoc);
  });
})();
